<template>
  <Teleport to="body">
    <v-navigation-drawer
      v-bind="$attrs"
      v-model="showDrawer"
      :data-testid="sectionTag"
      location="right"
      temporary
      :order="drawerOrder"
      :class="drawerClass"
      :style="{ maxWidth: props.maxWidth }"
    >
      <v-sheet class="container">
        <div class="drawer-header">
          <v-row class="ma-0">
            <v-col cols="auto" class="pa-0 d-flex flex-column">
              <slot name="header">
                <h3 v-if="headLine" class="head-line" :data-testid="sectionTag ? `${sectionTag}-title` : undefined">
                  {{ headLine }}
                </h3>
              </slot>
              <span v-if="subHeadLine" class="body-regular-large sub-head-line">{{ subHeadLine }}</span>
            </v-col>
            <v-spacer />
            <v-col cols="auto" class="pa-0 d-flex align-center ga-2">
              <slot name="header-actions" />
              <WButton
                :section-tag="sectionTag ? `${sectionTag}-close` : undefined"
                variant="icon"
                :icon="mdiClose"
                :label="$t('app.common.buttons.close')"
                class="btn-close"
                @click="close()"
              />
            </v-col>
          </v-row>
        </div>
        <div
          class="drawer-body"
          :class="{ 'drawer-body--horizontal-overflow-hidden': props.horizontalOverflow === 'hidden' }"
        >
          <CustomScrollbar
            :wrapper-style="{ height: '100%' }"
            :style="{ height: '100%', overflowX: props.horizontalOverflow }"
          >
            <v-sheet>
              <slot></slot>
            </v-sheet>
          </CustomScrollbar>
        </div>
      </v-sheet>
    </v-navigation-drawer>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiClose } from '@mdi/js'

import CustomScrollbar from 'custom-vue-scrollbar'
import { $t } from '@/plugins/i18n'
import { WButton } from '@wasabi/actions'
import {
  acquireBodyScrollLock,
  acquireDrawerStackOrder,
  registerOpenDrawer,
  releaseBodyScrollLock,
  releaseDrawerStackOrder,
  unregisterOpenDrawer
} from '@/composables/useDrawerScrollLock'

defineOptions({ inheritAttrs: false })

export interface WDrawerProps {
  sectionTag?: string
  modelValue?: boolean
  headLine?: string
  subHeadLine?: string
  maxWidth?: string
  /**
   * Controls horizontal overflow at the outer drawer scroll boundary.
   * Use `hidden` only when slotted content wraps or provides its own horizontal scroller.
   */
  horizontalOverflow?: 'auto' | 'hidden'
}

const props = withDefaults(defineProps<WDrawerProps>(), {
  sectionTag: 'unspecified',
  modelValue: undefined,
  headLine: '',
  subHeadLine: '',
  maxWidth: '1000px',
  horizontalOverflow: 'auto'
})

const emit = defineEmits(['update:modelValue', 'close'])

const showDrawer = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const drawerClass = computed(() => (showDrawer.value ? 'drawer show' : 'drawer'))
const drawerOrder = ref(-1)

const close = (): void => {
  emit('close')
  showDrawer.value = false
}

const handleEscape = (e: KeyboardEvent): void => {
  if (e.key === 'Escape') close()
}

const handleHashChange = (): void => {
  if (showDrawer.value) close()
}

// Per-instance flag: did THIS drawer currently acquire the body-scroll lock?
// Used to make `lockBodyScroll(true/false)` idempotent so the immediate-watch
// firing with `isOpen=false` (initial mount) doesn't decrement the shared
// counter incorrectly, and so unmount-while-open releases exactly once.
let hasLock = false
let hasStackOrder = false

const lockBodyScroll = (lock: boolean): void => {
  if (lock && !hasLock) {
    acquireBodyScrollLock()
    hasLock = true
  } else if (!lock && hasLock) {
    releaseBodyScrollLock()
    hasLock = false
  }
}

const updateDrawerStack = (isOpen: boolean): void => {
  if (isOpen && !hasStackOrder) {
    drawerOrder.value = acquireDrawerStackOrder()
    hasStackOrder = true
  } else if (!isOpen && hasStackOrder) {
    releaseDrawerStackOrder(drawerOrder.value)
    hasStackOrder = false
  }
}

watch(
  showDrawer,
  (isOpen) => {
    if (isOpen) {
      registerOpenDrawer(close)
      updateDrawerStack(true)
      document.addEventListener('keydown', handleEscape)
      window.addEventListener('hashchange', handleHashChange)
      lockBodyScroll(true)
    } else {
      unregisterOpenDrawer(close)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('hashchange', handleHashChange)
      lockBodyScroll(false)
      updateDrawerStack(false)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  unregisterOpenDrawer(close)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('hashchange', handleHashChange)
  // Defensive: restore scroll if the component unmounts while the drawer is
  // still considered open (e.g. parent route change).
  lockBodyScroll(false)
  updateDrawerStack(false)
})
</script>

<style scoped lang="scss">
// Vuetify's layout system pushes any v-navigation-drawer inside <v-app> down
// by the app bar's height (54px) and shrinks it accordingly. We want this
// drawer to cover the full viewport regardless, so override the inline styles.
.drawer {
  top: 0 !important;
  height: 100vh !important;
  max-height: 100vh !important;
  margin: 0;
  padding: 0;
  border-radius: var(--border-radius-layer-extra-large) 0 0 var(--border-radius-layer-extra-large);
  overflow: hidden;
  fill: rgb(var(--v-theme-white));
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

:deep(.v-navigation-drawer__scrim) {
  position: fixed;
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.drawer.show {
  width: 100vw !important;
}

// Suppresses Vuetify's native drawer overflow so CustomScrollbar is the sole
// scroll container for the body content.
:deep(.v-navigation-drawer__content) {
  overflow: hidden !important;
}

.container {
  margin: 0;
  padding: 12px;
  // Prevents the custom scroll thumb from overflowing the container bounds.
  overflow: hidden !important;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.drawer-header {
  flex-shrink: 0;
}

.drawer-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// custom-vue-scrollbar always renders both axes and decides visibility from
// scrollWidth/scrollHeight. Keep horizontal recovery available by default.
// Consumers that guarantee wrapping (or provide a nested horizontal scroller)
// may suppress gutter-only overflow and its phantom thumb explicitly.
.drawer-body--horizontal-overflow-hidden > :deep(.scrollbar__wrapper > .scrollbar__thumbPlaceholder--horizontal) {
  display: none !important;
}

@media only screen and (min-width: 768px) {
  .drawer.show {
    width: 75vw !important;
    filter: drop-shadow(0px 4px 10px rgba(53, 53, 53, 0.375));
  }
  .container {
    padding: 25px 22px;
  }
}

.head-line {
  color: rgb(var(--v-theme-neutral1000));
}

.sub-head-line {
  color: rgb(var(--v-theme-neutral1000));
}

.btn-close {
  width: 25px !important;
  height: 25px !important;
  min-width: 25px !important;
  min-height: 25px !important;
}

@media (pointer: none), (pointer: coarse) {
  :deep(.v-navigation-drawer) {
    top: 50px !important;
  }
}
</style>

<!-- Unscoped: targets <html> globally. Used by the shared scroll-lock counter
     in <script setup>. `!important` is required so Vuetify's overlay (which
     also writes inline `overflow` on <html>) doesn't override us when one
     drawer closes while another is still open. -->
<style>
html.w-drawer-scroll-lock {
  overflow: hidden !important;
}
</style>
